import { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';
import { supabase } from '@saas-boilerplate/shared';
import { useAuth } from '@/contexts/auth-context';
import { invokeEdgeFn } from '@/lib/edge-functions';
import { listCalendarEvents } from '@/lib/calendar-cache';
import { timed } from '@/lib/perf';
import { useAgentChatOpener } from '@/components/agent-chat';
import { Button } from './ui/button';
import { AppTopBar } from './app-top-bar';
import { CheckInRecorder, CheckInList } from './check-in-recorder';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { cn } from './ui/utils';
import { Tooltip, TooltipTrigger, TooltipContent } from './ui/tooltip';
import {
  BookOpen,
  Calendar,
  CheckCircle2,
  Cloud,
  Gavel,
  Inbox,
  ListTodo,
  PlayCircle,
  Sparkles,
  Video,
  Loader2,
  ArrowRight,
  MessageSquare,
  Plus,
} from 'lucide-react';

type DailyBriefingPanelProps = {
  briefing: {
    date_label: string;
    meetings: {
      meeting_id: string;
      series_title: string;
      starts_at: string;
      ends_at: string | null;
      role: 'owner' | 'attendee';
      organizer_name: string | null;
      accepted_count: number;
      declined_count: number;
      pending_count: number;
      agenda_count: number;
      open_action_count: number;
    }[];
    agenda_updates: { meeting_id: string; meeting_title: string; item_title: string }[];
    outstanding_actions: {
      id: string;
      title: string;
      meeting_id: string;
      meeting_title: string;
      due_at: string | null;
      overdue_days: number;
      /** Next upcoming occurrence of the action's series (ISO); drives "due today" grouping. */
      next_meeting_at?: string | null;
    }[];
    attention: { overdue_actions: number; overdue_actions_7d: number };
  } | null;
  loading: boolean;
  timezone: string;
  /** Counts per age bucket: [0–1d, 2–7d, 8–14d, 15–30d, 30+d]. Older buckets render darker. */
  actionAgeBuckets: number[];
  onOpenMeeting?: (meetingId: string) => void;
  onAllActions?: () => void;
  /** Google events (for the displayed day) not yet enrolled - the third "Not enrolled" lane. */
  notEnrolled?: NotEnrolledEvent[];
  onEnrol?: (ev: NotEnrolledEvent) => void;
  enrollingId?: string | null;
  /** Meetings for the displayed calendar-graph day (today or a paged day), enriched with my action count. */
  timelineMeetings: {
    starts_at: string;
    ends_at: string | null;
    series_title?: string;
    meeting_id?: string;
    accepted_count?: number;
    declined_count?: number;
    pending_count?: number;
    agenda_count?: number;
    open_action_count?: number;
    my_open_action_count?: number;
  }[];
  dayOffset?: number;
  onPrevDay?: () => void;
  onNextDay?: () => void;
  dayLoading?: boolean;
  /** User's configured working hours, drives the Today-graph window. */
  workingHours?: { start: string; end: string };
  /** Whether the user has any calendar connected - tailors the empty-timeline message. */
  hasCalendarConnection?: boolean;
  /** Enrol-a-meeting CTA used by the empty-timeline state. */
  onEnrolMeeting?: () => void;
  /** Connect-a-calendar CTA used by the empty-timeline state (no calendar connected). */
  onConnectCalendar?: () => void;
};

/**
 * Builds the timeline blocks for a workday (8am–7pm local). Sorts meetings, clamps to the window,
 * merges overlapping busy ranges, then enumerates the gaps. Each gap ≥ 30 minutes is a focus
 * block worth surfacing.
 */
type TimelineBlock = { kind: 'busy' | 'focus'; startMs: number; endMs: number };
function parseHhMm(s: string, fallbackHour: number): { h: number; m: number } {
  const match = /^(\d{1,2}):(\d{2})$/.exec(s || '');
  if (!match) return { h: fallbackHour, m: 0 };
  return { h: Math.max(0, Math.min(23, Number(match[1]))), m: Math.max(0, Math.min(59, Number(match[2]))) };
}
function buildFocusBlocks(
  meetings: { starts_at: string; ends_at: string | null }[],
  baseDate: Date = new Date(),
  workingHours: { start: string; end: string } = { start: '08:00', end: '19:00' },
): { blocks: TimelineBlock[]; dayStart: number; dayEnd: number; nowMs: number } {
  const startHm = parseHhMm(workingHours.start, 8);
  const endHm = parseHhMm(workingHours.end, 19);
  const dayStart = new Date(baseDate);
  dayStart.setHours(startHm.h, startHm.m, 0, 0);
  const dayEnd = new Date(baseDate);
  dayEnd.setHours(endHm.h, endHm.m, 0, 0);
  const startMs = dayStart.getTime();
  const endMs = dayEnd.getTime();
  const ranges = meetings
    .map((m) => {
      const s = new Date(m.starts_at).getTime();
      const e = m.ends_at ? new Date(m.ends_at).getTime() : s + 30 * 60_000;
      return {
        start: Math.max(startMs, Math.min(endMs, s)),
        end: Math.max(startMs, Math.min(endMs, e)),
      };
    })
    .filter((r) => Number.isFinite(r.start) && Number.isFinite(r.end) && r.end > r.start)
    .sort((a, b) => a.start - b.start);
  const merged: { start: number; end: number }[] = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && last.end >= r.start) {
      last.end = Math.max(last.end, r.end);
    } else {
      merged.push({ ...r });
    }
  }
  const blocks: TimelineBlock[] = [];
  let cursor = startMs;
  for (const r of merged) {
    if (r.start > cursor) blocks.push({ kind: 'focus', startMs: cursor, endMs: r.start });
    blocks.push({ kind: 'busy', startMs: r.start, endMs: r.end });
    cursor = r.end;
  }
  if (cursor < endMs) blocks.push({ kind: 'focus', startMs: cursor, endMs: endMs });
  return { blocks, dayStart: startMs, dayEnd: endMs, nowMs: Date.now() };
}

/**
 * Lays out time bars that may overlap into vertical sub-rows. Within each cluster of
 * transitively-overlapping bars, each bar gets a column index `col` and the cluster's total
 * column count `cols`, so two meetings at the same time render stacked instead of on top of each
 * other. Non-overlapping bars get `cols: 1` (full lane height).
 */
function packBars<T extends { start: number; end: number }>(bars: T[]): (T & { col: number; cols: number })[] {
  const sorted = [...bars].sort((a, b) => a.start - b.start || a.end - b.end);
  const out: (T & { col: number; cols: number })[] = [];
  let cluster: (T & { col: number })[] = [];
  let clusterEnd = -Infinity;
  let colEnds: number[] = [];
  const flush = () => {
    const cols = cluster.reduce((mx, c) => Math.max(mx, c.col + 1), 0);
    for (const c of cluster) out.push({ ...c, cols });
    cluster = [];
    colEnds = [];
    clusterEnd = -Infinity;
  };
  for (const b of sorted) {
    if (cluster.length && b.start >= clusterEnd) flush(); // disjoint from current cluster
    let col = colEnds.findIndex((e) => e <= b.start);
    if (col === -1) col = colEnds.length;
    colEnds[col] = b.end;
    cluster.push({ ...b, col });
    clusterEnd = Math.max(clusterEnd, b.end);
  }
  flush();
  return out;
}

/** "29th May" - ordinal day + short month, for the calendar-graph day nav. */
function formatDayNav(date: Date): string {
  const day = date.getDate();
  const k = day % 100;
  const j = day % 10;
  const suffix = k >= 11 && k <= 13 ? 'th' : j === 1 ? 'st' : j === 2 ? 'nd' : j === 3 ? 'rd' : 'th';
  return `${day}${suffix} ${date.toLocaleDateString('en-GB', { month: 'short' })}`;
}
/** Day label for an offset from today: Today / Tomorrow / "29th May". */
function dayOffsetLabel(offset: number): string {
  if (offset <= 0) return 'Today';
  if (offset === 1) return 'Tomorrow';
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return formatDayNav(d);
}

function FocusBlocksTimeline({
  meetings,
  timezone,
  onOpenMeeting,
  notEnrolled,
  onEnrol,
  enrollingId,
  dayOffset = 0,
  onPrevDay,
  onNextDay,
  dayLoading = false,
  workingHours,
  hasCalendarConnection,
  onEnrolMeeting,
  onConnectCalendar,
}: {
  meetings: {
    starts_at: string;
    ends_at: string | null;
    series_title?: string;
    meeting_id?: string;
    accepted_count?: number;
    declined_count?: number;
    pending_count?: number;
    agenda_count?: number;
    open_action_count?: number;
    /** Outstanding actions assigned to me across this meeting's series (not just this occurrence). */
    my_open_action_count?: number;
  }[];
  timezone: string;
  onOpenMeeting?: (meetingId: string) => void;
  notEnrolled?: NotEnrolledEvent[];
  onEnrol?: (ev: NotEnrolledEvent) => void;
  enrollingId?: string | null;
  /** 0 = today, 1 = tomorrow, … - which day the graph is showing. */
  dayOffset?: number;
  onPrevDay?: () => void;
  onNextDay?: () => void;
  dayLoading?: boolean;
  /** User's configured working hours, drives the graph window. Defaults to 08:00–19:00. */
  workingHours?: { start: string; end: string };
  hasCalendarConnection?: boolean;
  onEnrolMeeting?: () => void;
  onConnectCalendar?: () => void;
}) {
  // The day this graph is rendering, clipped to the user's working hours.
  const baseDate = new Date();
  baseDate.setDate(baseDate.getDate() + dayOffset);

  // Free time should account for ALL calendar commitments that day - enrolled and not - so the
  // green lane doesn't show "free" under an un-enrolled meeting.
  const notEnrolledList = notEnrolled || [];
  const busyForFree = [
    ...meetings,
    ...notEnrolledList.map((n) => ({ starts_at: n.starts_at, ends_at: n.ends_at })),
  ];
  const { blocks, dayStart, dayEnd, nowMs } = buildFocusBlocks(busyForFree, baseDate, workingHours);
  const dayMs = dayEnd - dayStart;
  const fmtDur = (mins: number) => {
    if (mins < 60) return `${mins}m`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  };
  const fmtClock = (ms: number) =>
    new Date(ms).toLocaleTimeString('en-GB', { timeZone: timezone, hour: '2-digit', minute: '2-digit' });

  // Hour ticks across the workday window (08:00 … 19:00).
  const pctFor = (ms: number) => ((ms - dayStart) / dayMs) * 100;
  const startHour = new Date(dayStart).getHours();
  const endHour = new Date(dayEnd).getHours();
  const hourTicks: { hour: number; pct: number; label: string }[] = [];
  for (let h = startHour; h <= endHour; h++) {
    const tickMs = new Date(dayStart).setHours(h, 0, 0, 0);
    hourTicks.push({ hour: h, pct: pctFor(tickMs), label: `${String(h).padStart(2, '0')}:00` });
  }
  // Keep the first/last labels inside the chart edges instead of centring (which would clip).
  const tickTransform = (i: number) =>
    i === 0 ? 'none' : i === hourTicks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)';

  // Individual meeting bars (clamped to the window) so each pill can show its own name.
  // Free windows still come from the merged gaps so back-to-back meetings don't fake one.
  const meetingBars = meetings
    .map((m) => {
      const s = new Date(m.starts_at).getTime();
      const e = m.ends_at ? new Date(m.ends_at).getTime() : s + 30 * 60_000;
      const accepted = m.accepted_count ?? 0;
      const invited = accepted + (m.declined_count ?? 0) + (m.pending_count ?? 0);
      return {
        title: (m.series_title || 'Meeting').trim() || 'Meeting',
        meetingId: m.meeting_id || null,
        start: Math.max(dayStart, Math.min(dayEnd, s)),
        end: Math.max(dayStart, Math.min(dayEnd, e)),
        accepted,
        invited,
        agendaCount: m.agenda_count ?? 0,
        openActions: m.my_open_action_count ?? m.open_action_count ?? 0,
      };
    })
    .filter((r) => Number.isFinite(r.start) && Number.isFinite(r.end) && r.end > r.start)
    .sort((a, b) => a.start - b.start);
  const freeBlocks = blocks
    .filter((b) => b.kind === 'focus' && b.endMs - b.startMs >= 30 * 60_000)
    .map((b) => ({ ...b, mins: Math.round((b.endMs - b.startMs) / 60_000) }));
  const focusTotalMin = freeBlocks.reduce((acc, b) => acc + b.mins, 0);

  // Not-enrolled bars (today's Google events not on Upstand), clamped to the window.
  const notEnrolledBars = notEnrolledList
    .map((n) => {
      const s = new Date(n.starts_at).getTime();
      const e = n.ends_at ? new Date(n.ends_at).getTime() : s + 30 * 60_000;
      return {
        ev: n,
        title: (n.summary || 'Meeting').trim() || 'Meeting',
        start: Math.max(dayStart, Math.min(dayEnd, s)),
        end: Math.max(dayStart, Math.min(dayEnd, e)),
      };
    })
    .filter((r) => Number.isFinite(r.start) && Number.isFinite(r.end) && r.end > r.start)
    .sort((a, b) => a.start - b.start);

  const nowVisible = nowMs >= dayStart && nowMs <= dayEnd;
  const nowPct = nowVisible ? pctFor(nowMs) : 0;

  const LANE_H = 30; // px height of each pill lane
  const LANE_GAP = 12; // px gap between lanes
  const SUB_GAP = 2; // gap between stacked sub-rows when bars in a lane overlap
  const hasNotEnrolled = notEnrolledBars.length > 0;
  const laneCount = hasNotEnrolled ? 3 : 2; // Enrolled, (Available to enrol), Free
  const chartHeight = laneCount * LANE_H + (laneCount - 1) * LANE_GAP;
  // Row order, top → bottom: enrolled meetings, meetings available to enrol, then free time.
  const meetingTop = 0;
  const notEnrolledTop = LANE_H + LANE_GAP;
  const freeTop = (hasNotEnrolled ? 2 : 1) * (LANE_H + LANE_GAP);
  // Split overlapping bars into vertical sub-rows so same-time meetings don't stack on top of each other.
  const meetingBarsLaid = packBars(meetingBars);
  const notEnrolledBarsLaid = packBars(notEnrolledBars);
  const laneSlot = (laneTop: number, col: number, cols: number) => {
    const subH = (LANE_H - (cols - 1) * SUB_GAP) / Math.max(1, cols);
    return { top: laneTop + col * (subH + SUB_GAP), height: subH };
  };

  // No enrolled meetings and no un-enrolled calendar events for the day → show a purposeful empty
  // state instead of a bare all-day "Free" bar (DR-14).
  const isEmpty = meetingBars.length === 0 && notEnrolledBars.length === 0;

  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2">
          {dayOffset > 0 && onPrevDay && (
            <button
              type="button"
              onClick={onPrevDay}
              className="font-display text-lg font-semibold leading-none text-headline transition-colors hover:text-primary"
              aria-label="Previous day"
            >
              ‹
            </button>
          )}
          <h3 className="font-display text-lg font-semibold text-headline">{dayOffsetLabel(dayOffset)}</h3>
          {dayLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-tertiary" aria-hidden />}
        </div>
        {onNextDay && (
          <button
            type="button"
            onClick={onNextDay}
            className="font-display text-lg font-semibold text-headline underline underline-offset-4 transition-colors hover:text-primary"
          >
            {dayOffsetLabel(dayOffset + 1)} ›
          </button>
        )}
      </div>

      {/* Top hour axis + the Now marker pill. */}
      <div className="relative mb-1.5 h-5 text-[10px] tabular-nums text-muted-foreground">
        {hourTicks.map((t, i) => (
          <span
            key={`top-${t.hour}`}
            className="absolute top-0 whitespace-nowrap"
            style={{ left: `${t.pct}%`, transform: tickTransform(i) }}
          >
            {t.label}
          </span>
        ))}
        {nowVisible && (
          <span
            className="absolute bottom-0 z-20 -translate-x-1/2 rounded bg-foreground px-1.5 py-0.5 text-[10px] font-medium leading-none text-background shadow-sm"
            style={{ left: `${nowPct}%` }}
          >
            Now
          </span>
        )}
      </div>

      {/* Chart body: hour gridlines, pill lanes (Free / Meeting / Not enrolled), and the Now line. */}
      <div className="relative" style={{ height: isEmpty ? '150px' : `${chartHeight}px` }}>
        {hourTicks.map((t) => (
          <div
            key={`grid-${t.hour}`}
            className="absolute top-0 h-full w-px bg-outline-variant/30"
            style={{ left: `${t.pct}%` }}
            aria-hidden
          />
        ))}

        {isEmpty ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
            {hasCalendarConnection ? (
              <>
                <p className="text-sm font-medium text-foreground">Nothing scheduled - enjoy the space.</p>
                <p className="max-w-sm text-xs text-muted-foreground">Enrol a meeting to track its agenda, notes and actions right here.</p>
                {onEnrolMeeting && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={onEnrolMeeting}
                    className="mt-1 gap-1.5 rounded-full bg-gradient-to-r from-primary to-primary-container text-xs font-medium text-primary-foreground hover:opacity-90"
                  >
                    <Plus className="h-3.5 w-3.5" /> Enrol a meeting
                  </Button>
                )}
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-foreground">See your whole day at a glance</p>
                <p className="max-w-sm text-xs text-muted-foreground">Connect a calendar and your meetings appear here automatically - ready to enrol in a click.</p>
                {onConnectCalendar && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={onConnectCalendar}
                    className="mt-1 gap-1.5 rounded-full bg-gradient-to-r from-primary to-primary-container text-xs font-medium text-primary-foreground hover:opacity-90"
                  >
                    <Calendar className="h-3.5 w-3.5" /> Connect a calendar
                  </Button>
                )}
              </>
            )}
          </div>
        ) : (
          <>
        {/* Free windows - bottom lane. Soft green chip to sit with the warm palette. */}
        {freeBlocks.map((b) => (
          <Tooltip key={`free-${b.startMs}`}>
            <TooltipTrigger asChild>
              <div
                className="absolute flex items-center overflow-hidden rounded-md bg-emerald-100 px-2.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-200"
                style={{
                  left: `${pctFor(b.startMs)}%`,
                  width: `${Math.max(0, pctFor(b.endMs) - pctFor(b.startMs))}%`,
                  top: `${freeTop}px`,
                  height: `${LANE_H}px`,
                }}
              >
                <span className="truncate">Free</span>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              Free · {fmtClock(b.startMs)}–{fmtClock(b.endMs)} · {fmtDur(b.mins)}
            </TooltipContent>
          </Tooltip>
        ))}

        {/* Enrolled meetings - top lane. Each pill shows its (truncated) name, full name on hover,
            and opens the meeting when clicked. */}
        {meetingBarsLaid.map((b, i) => {
          const clickable = Boolean(b.meetingId && onOpenMeeting);
          const slot = laneSlot(meetingTop, b.col, b.cols);
          return (
            <Tooltip key={`busy-${b.start}-${i}`}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={clickable ? () => onOpenMeeting!(b.meetingId!) : undefined}
                  className={cn(
                    'absolute flex items-center overflow-hidden rounded-md bg-stone-100 px-2.5 text-left text-[11px] font-medium text-stone-600 ring-1 ring-inset ring-stone-200/80 transition-colors',
                    clickable ? 'cursor-pointer hover:bg-stone-200 hover:text-stone-800' : 'cursor-default',
                  )}
                  style={{
                    left: `${pctFor(b.start)}%`,
                    width: `${Math.max(0, pctFor(b.end) - pctFor(b.start))}%`,
                    top: `${slot.top}px`,
                    height: `${slot.height}px`,
                  }}
                >
                  <span className="truncate">{b.title}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[240px]">
                <div className="space-y-0.5 leading-tight">
                  <div className="font-semibold">{b.title}</div>
                  <div>{fmtClock(b.start)}–{fmtClock(b.end)}</div>
                  <div>{b.invited > 0 ? `${b.accepted}/${b.invited} Accepted` : 'No invitees yet'}</div>
                  <div>{b.openActions} Action item{b.openActions === 1 ? '' : 's'} open for you</div>
                  <div>{b.agendaCount} Agenda item{b.agendaCount === 1 ? '' : 's'}</div>
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}

        {/* Not-enrolled - bottom lane. Dashed amber chip; click to enrol in one step. */}
        {notEnrolledBarsLaid.map((b, i) => {
          const enrolling = enrollingId === b.ev.id;
          const canEnrol = Boolean(onEnrol) && !enrolling;
          const slot = laneSlot(notEnrolledTop, b.col, b.cols);
          return (
            <Tooltip key={`ne-${b.start}-${i}`}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={canEnrol ? () => onEnrol!(b.ev) : undefined}
                  disabled={enrolling}
                  className={cn(
                    'absolute flex items-center gap-1 overflow-hidden rounded-md border border-dashed border-amber-400 bg-amber-50 px-2 text-left text-[11px] font-medium text-amber-800 transition-colors',
                    canEnrol ? 'cursor-pointer hover:bg-amber-100' : 'cursor-default',
                  )}
                  style={{
                    left: `${pctFor(b.start)}%`,
                    width: `${Math.max(0, pctFor(b.end) - pctFor(b.start))}%`,
                    top: `${slot.top}px`,
                    height: `${slot.height}px`,
                  }}
                >
                  {enrolling ? (
                    <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
                  ) : (
                    <Plus className="h-3 w-3 shrink-0" aria-hidden />
                  )}
                  <span className="truncate">{b.title}</span>
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[240px]">
                <div className="space-y-0.5 leading-tight">
                  <div className="font-semibold">{b.title}</div>
                  <div>{fmtClock(b.start)}–{fmtClock(b.end)}</div>
                  <div>{enrolling ? 'Enrolling…' : 'Not on Upstand - click to enrol'}</div>
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}

        {/* Now line through the whole chart. */}
        {nowVisible && (
          <div
            className="pointer-events-none absolute top-0 z-10 h-full w-px bg-foreground/60"
            style={{ left: `${nowPct}%` }}
            aria-label="Now"
          />
        )}
        </>
        )}
      </div>

      {/* Bottom hour axis. */}
      <div className="relative mt-1.5 h-4 text-[10px] tabular-nums text-muted-foreground">
        {hourTicks.map((t, i) => (
          <span
            key={`bot-${t.hour}`}
            className="absolute top-0 whitespace-nowrap"
            style={{ left: `${t.pct}%`, transform: tickTransform(i) }}
          >
            {t.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function ActionAgeHistogram({
  buckets,
  onAllActions,
}: {
  buckets: number[];
  onAllActions?: (ageBucket?: string) => void;
}) {
  // Input buckets ordered youngest → oldest: [0–1d, 2–7d, 8–14d, 15–30d, 30+d].
  // Render them OLDEST first so the high-priority "30+d" pile is in the most prominent slot
  // (leftmost). Older = darker amber.
  const display = [
    { key: '30+', label: '30+d', count: buckets[4] ?? 0, color: 'bg-amber-900' },
    { key: '15-30', label: '15–30d', count: buckets[3] ?? 0, color: 'bg-amber-700' },
    { key: '8-14', label: '8–14d', count: buckets[2] ?? 0, color: 'bg-amber-500' },
    { key: '2-7', label: '2–7d', count: buckets[1] ?? 0, color: 'bg-amber-400' },
    { key: '0-1', label: '0–1d', count: buckets[0] ?? 0, color: 'bg-amber-200' },
  ];
  const total = display.reduce((a, b) => a + b.count, 0);
  const maxCount = Math.max(1, ...display.map((d) => d.count));
  const TRACK_PX = 110; // total drawable bar height
  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <h3 className="font-display text-lg font-semibold text-headline">Open actions by age</h3>
          {total > 0 && (
            <span className="inline-flex items-center justify-center rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
              {total}
            </span>
          )}
        </div>
        {total > 0 && onAllActions && (
          <button
            type="button"
            onClick={() => onAllActions()}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            View all <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
      {total === 0 && (
        <p className="text-xs text-tertiary">No outstanding actions assigned to you.</p>
      )}
      {total > 0 && (
        <div>
          <div className="flex items-end gap-2" style={{ height: `${TRACK_PX + 18}px` }}>
            {display.map((d) => {
              const barPx = d.count > 0
                ? Math.max(10, Math.round((d.count / maxCount) * TRACK_PX))
                : 3;
              const drillable = d.count > 0 && Boolean(onAllActions);
              return (
                <button
                  key={d.label}
                  type="button"
                  onClick={drillable ? () => onAllActions!(d.key) : undefined}
                  disabled={!drillable}
                  aria-label={`${d.count} open actions ${d.label} old`}
                  title={`${d.count} open action${d.count === 1 ? '' : 's'} ${d.label} old${drillable ? ' - click to view' : ''}`}
                  className={cn(
                    'group flex h-full flex-1 flex-col items-center justify-end rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
                    drillable ? 'cursor-pointer' : 'cursor-default',
                  )}
                >
                  <span
                    className={cn(
                      'mb-1 text-xs font-semibold tabular-nums leading-none',
                      d.count > 0 ? 'text-foreground' : 'text-muted-foreground/40',
                    )}
                  >
                    {d.count}
                  </span>
                  <div
                    className={cn(
                      'w-full rounded-t transition-opacity',
                      d.count > 0 ? d.color : 'bg-muted/30',
                      drillable && 'group-hover:opacity-80',
                    )}
                    style={{ height: `${barPx}px` }}
                  />
                </button>
              );
            })}
          </div>
          <div className="mt-1.5 flex gap-2">
            {display.map((d) => (
              <span
                key={d.label}
                className={cn(
                  'flex-1 text-center text-[10px] tabular-nums',
                  d.count > 0 ? 'text-foreground' : 'text-muted-foreground',
                )}
              >
                {d.label}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * "Action items for today's meetings" - today's meetings paired with the outstanding actions
 * assigned to me that belong to the same series (actions are generally due on the day of their
 * meeting). Same action source as the My Day email's outstanding-actions section; matched to
 * today's meetings by series title so a meeting that already happened this morning still counts.
 */
function ActionsDueTodayBlock({
  meetings,
  actions,
  timezone,
  onOpenMeeting,
  onAllActions,
}: {
  meetings: { meeting_id: string; series_title: string; starts_at: string }[];
  actions: {
    id: string;
    title: string;
    meeting_id: string;
    meeting_title: string;
    due_at: string | null;
    overdue_days: number;
    next_meeting_at?: string | null;
  }[];
  timezone: string;
  onOpenMeeting?: (meetingId: string) => void;
  onAllActions?: () => void;
}) {
  // Relative "today @ 12:00" / "tomorrow @ 09:30" / "2 Jun @ 16:00"; drops the clock for
  // midnight (date-only) values so they don't read "@ 00:00".
  const relWhen = (iso: string | null): string => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const keyOf = (x: Date) => x.toLocaleDateString('en-CA', { timeZone: timezone });
      const now = new Date();
      const tmr = new Date(now.getTime() + 86400000);
      let day: string;
      if (keyOf(d) === keyOf(now)) day = 'today';
      else if (keyOf(d) === keyOf(tmr)) day = 'tomorrow';
      else day = d.toLocaleDateString('en-GB', { timeZone: timezone, day: 'numeric', month: 'short' });
      const time = d.toLocaleTimeString('en-GB', { timeZone: timezone, hour: '2-digit', minute: '2-digit' });
      return time === '00:00' ? day : `${day} @ ${time}`;
    } catch {
      return '';
    }
  };

  // Index my outstanding actions by the series (title) they belong to.
  const actionsByTitle = new Map<string, typeof actions>();
  for (const a of actions) {
    const key = a.meeting_title || 'Meeting';
    if (!actionsByTitle.has(key)) actionsByTitle.set(key, []);
    actionsByTitle.get(key)!.push(a);
  }

  // One card per today's meeting (chronological), but only when it has actions assigned to me.
  const seen = new Set<string>();
  const groups: { meeting_id: string; title: string; whenIso: string; items: typeof actions }[] = [];
  for (const m of [...meetings].sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime())) {
    const key = m.series_title || 'Meeting';
    if (seen.has(key)) continue;
    seen.add(key);
    const items = actionsByTitle.get(key) || [];
    if (items.length === 0) continue;
    groups.push({ meeting_id: m.meeting_id, title: key, whenIso: m.starts_at, items });
  }
  const shownCount = groups.reduce((n, g) => n + g.items.length, 0);

  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <h3 className="font-display text-lg font-semibold text-headline">Action items for today&apos;s meetings</h3>
          {shownCount > 0 && (
            <span className="inline-flex items-center justify-center rounded-full bg-muted px-2 py-0.5 text-xs font-semibold tabular-nums text-muted-foreground">
              {shownCount}
            </span>
          )}
        </div>
        {onAllActions && (
          <button
            type="button"
            onClick={() => onAllActions()}
            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
          >
            View all <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>
      {groups.length === 0 ? (
        <p className="text-sm italic text-muted-foreground">No action items assigned to you for today&apos;s meetings.</p>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => {
            const clickable = Boolean(g.meeting_id && onOpenMeeting);
            return (
              <div key={g.title} className="rounded-lg border border-outline-variant/30 bg-white/50 p-3">
                <button
                  type="button"
                  onClick={clickable ? () => onOpenMeeting!(g.meeting_id) : undefined}
                  className={cn(
                    'mb-2 flex w-full items-baseline gap-2 text-left text-sm font-semibold text-foreground',
                    clickable ? 'cursor-pointer hover:text-primary' : 'cursor-default',
                  )}
                  title={g.title}
                >
                  <span className="truncate">{g.title}</span>
                  {relWhen(g.whenIso) && (
                    <span className="shrink-0 text-xs font-normal text-tertiary">{relWhen(g.whenIso)}</span>
                  )}
                </button>
                <ul className="space-y-1.5">
                  {g.items.map((a) => {
                    const due = relWhen(a.due_at);
                    return (
                      <li key={a.id} className="flex items-start gap-2 text-sm">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                        <span className="flex-1 text-foreground">
                          {a.title}
                          {due && <span className="text-tertiary"> · due {due}</span>}
                          {a.overdue_days > 0 && (
                            <span className="ml-1.5 rounded-full bg-rose-100 px-1.5 py-0.5 text-[10px] font-medium text-rose-700">
                              {a.overdue_days}d overdue
                            </span>
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DailyBriefingPanel({
  briefing,
  loading,
  timezone,
  actionAgeBuckets,
  onAllActions,
  onOpenMeeting,
  notEnrolled,
  onEnrol,
  enrollingId,
  timelineMeetings,
  dayOffset = 0,
  onPrevDay,
  onNextDay,
  dayLoading = false,
  workingHours,
  hasCalendarConnection,
  onEnrolMeeting,
  onConnectCalendar,
}: DailyBriefingPanelProps) {
  return (
    <div className="rounded-xl border border-white/40 bg-white/40 px-6 py-5 shadow-sm backdrop-blur-xl">
      {briefing?.attention.overdue_actions_7d ? (
        <div className="mb-4 flex justify-end">
          <span className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-100">
            ⚠ {briefing.attention.overdue_actions_7d} action{briefing.attention.overdue_actions_7d === 1 ? '' : 's'} overdue &gt; 1 week
          </span>
        </div>
      ) : null}

      {loading && !briefing ? (
        <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : !briefing ? null : (
        <div className="space-y-6">
          {/* Focus timeline - full width so the hour axis has room to breathe. */}
          <FocusBlocksTimeline
            meetings={timelineMeetings}
            timezone={timezone}
            onOpenMeeting={onOpenMeeting}
            notEnrolled={notEnrolled}
            onEnrol={onEnrol}
            enrollingId={enrollingId}
            dayOffset={dayOffset}
            onPrevDay={onPrevDay}
            onNextDay={onNextDay}
            dayLoading={dayLoading}
            workingHours={workingHours}
            hasCalendarConnection={hasCalendarConnection}
            onEnrolMeeting={onEnrolMeeting}
            onConnectCalendar={onConnectCalendar}
          />

          {/* Action items for today's meetings (left) + Open actions by age (right). */}
          <div className="grid grid-cols-1 gap-6 border-t border-outline-variant/20 pt-5 md:grid-cols-2 md:gap-8 md:divide-x md:divide-outline-variant/30">
            <div className="md:pr-8">
              <ActionsDueTodayBlock
                meetings={briefing.meetings}
                actions={briefing.outstanding_actions}
                timezone={timezone}
                onOpenMeeting={onOpenMeeting}
                onAllActions={onAllActions}
              />
            </div>
            <div className="md:pl-8">
              <ActionAgeHistogram buckets={actionAgeBuckets} onAllActions={onAllActions} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface DashboardProps {
  userProfile?: { full_name?: string; email?: string; role?: string } | null;
  workspaceName?: string | null;
  workspaceId?: string | null;
  onNavigateToMeetings?: () => void;
  onNavigateToIntegrations?: () => void;
  onNewMeeting?: () => void;
  /** Action Item Overview “View all”, Luminous CTA, etc. */
  onNavigateToActionItems?: () => void;
  /** Spotlight “View session” - opens Meetings on that series + meeting instance. */
  onOpenMeetingSession?: (meetingId: string) => void;
  onSignOut?: () => void;
  onSwitchWorkspace?: () => void;
  onHelp?: () => void;
}

type MeetingSeriesEmbed = { title?: string | null } | null;

type DashboardMeeting = {
  id: string;
  starts_at: string | null;
  ends_at: string | null;
  status: string;
  meeting_series: MeetingSeriesEmbed;
};

/** A Google-calendar event today that isn't enrolled in Upstand yet (drives the "Not enrolled" lane). */
type NotEnrolledEvent = {
  id: string;
  provider: 'google' | 'microsoft';
  summary: string;
  starts_at: string;
  ends_at: string | null;
  video_url: string | null;
  recurring_event_id: string | null;
  is_organizer: boolean;
};

function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

function greetingLabel(date: Date): string {
  const h = date.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function seriesTitle(m: DashboardMeeting): string {
  const t = m.meeting_series && typeof m.meeting_series === 'object' ? m.meeting_series.title : null;
  return (t && String(t).trim()) || 'Meeting';
}

function formatSpotlightDateTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function pickSpotlightMeetings(meetings: DashboardMeeting[]): DashboardMeeting[] {
  const now = Date.now();
  const sixHrAgo = now - 6 * 60 * 60 * 1000;
  const withStart = meetings.filter((m) => m.starts_at);
  const startMs = (m: DashboardMeeting) => new Date(m.starts_at!).getTime();
  const isLive = (m: DashboardMeeting) => {
    const s = startMs(m);
    const e = m.ends_at ? new Date(m.ends_at).getTime() : null;
    return m.status === 'in_progress' || (s <= now && e != null && now <= e);
  };
  // Rank so the two cards always favour what matters next: live meetings, then the soonest
  // upcoming, and only fall back to a just-ended meeting (within 6h) if there's nothing else.
  // A flat ascending sort previously let a meeting that ended a couple of hours ago sort *before*
  // an upcoming one and consume both slots - hiding the next meeting (DR: spotlight).
  const rank = (m: DashboardMeeting): number => {
    if (isLive(m)) return 0;
    if (startMs(m) > now) return 1; // upcoming
    if (startMs(m) >= sixHrAgo) return 2; // recently ended
    return 3; // older - last resort
  };
  const ordered = [...withStart].sort((a, b) => {
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    // Upcoming/live: soonest first. Past tiers: most recent first.
    return ra <= 1 ? startMs(a) - startMs(b) : startMs(b) - startMs(a);
  });
  return ordered.slice(0, 2);
}

function isMeetingUpcoming(m: DashboardMeeting): boolean {
  if (!m.starts_at) return false;
  return new Date(m.starts_at).getTime() > Date.now();
}

function meetingSpotlightBadge(m: DashboardMeeting): { label: string; className: string } {
  if (!m.starts_at) return { label: 'Scheduled', className: 'text-muted-foreground' };
  const now = Date.now();
  const start = new Date(m.starts_at).getTime();
  const end = m.ends_at ? new Date(m.ends_at).getTime() : null;
  // Only call a meeting "live" when it genuinely is - explicit status, or now sits inside the
  // start–end window. The old heuristic tagged anything started in the last 4h as "in progress",
  // which made multiple spotlight cards read the same with no real contrast (DR-12).
  if (m.status === 'in_progress' || (start <= now && end != null && now <= end)) {
    return { label: 'Live now', className: 'text-primary' };
  }
  const mins = Math.round((start - now) / 60000);
  if (mins > 0 && mins < 60) return { label: `Starts in ${mins}m`, className: 'text-primary' };
  if (mins >= 60 && mins < 24 * 60) return { label: `Starts in ${Math.round(mins / 60)}h`, className: 'text-muted-foreground' };
  if (mins >= 24 * 60) return { label: 'Upcoming', className: 'text-muted-foreground' };
  return { label: 'Ended', className: 'text-muted-foreground' };
}

export function Dashboard({
  userProfile,
  workspaceName,
  workspaceId,
  onNavigateToMeetings,
  onNavigateToIntegrations,
  onNewMeeting,
  onNavigateToActionItems,
  onOpenMeetingSession,
  onSignOut,
  onSwitchWorkspace,
  onHelp,
}: DashboardProps) {
  const { user } = useAuth();
  const firstName = useMemo(() => {
    const n = userProfile?.full_name?.trim();
    if (!n) return 'there';
    return n.split(/\s+/)[0] || 'there';
  }, [userProfile?.full_name]);

  const openAgent = useAgentChatOpener();

  const [loading, setLoading] = useState(true);
  const [hasActivity, setHasActivity] = useState(false);
  const [meetingsTodayCount, setMeetingsTodayCount] = useState(0);
  const [openActionCount, setOpenActionCount] = useState(0);
  const [mineOpenActionCount, setMineOpenActionCount] = useState(0);
  /** Counts per age bucket: [0–1d, 2–7d, 8–14d, 15–30d, 30+d]. Older buckets render darker. */
  const [actionAgeBuckets, setActionAgeBuckets] = useState<number[]>([0, 0, 0, 0, 0]);
  const [spotlightMeetings, setSpotlightMeetings] = useState<DashboardMeeting[]>([]);
  const [topActiveMeetings, setTopActiveMeetings] = useState<
    { meeting_id: string; title: string; starts_at: string | null; actions: number; agenda: number; decisions: number; total: number }[]
  >([]);
  const [weeklyStats, setWeeklyStats] = useState<{ meetings: number; actionsClosed: number; decisions: number }>({
    meetings: 0,
    actionsClosed: 0,
    decisions: 0,
  });

  const [checkInMeeting, setCheckInMeeting] = useState<DashboardMeeting | null>(null);
  const [checkInList, setCheckInList] = useState<any[]>([]);
  const [checkInLoading, setCheckInLoading] = useState(false);

  type BriefingMeeting = {
    meeting_id: string;
    series_title: string;
    starts_at: string;
    ends_at: string | null;
    role: 'owner' | 'attendee';
    organizer_name: string | null;
    accepted_count: number;
    declined_count: number;
    pending_count: number;
    agenda_count: number;
    open_action_count: number;
  };
  type BriefingAction = {
    id: string;
    title: string;
    meeting_id: string;
    meeting_title: string;
    due_at: string | null;
    overdue_days: number;
  };
  type BriefingAgendaUpdate = {
    meeting_id: string;
    meeting_title: string;
    item_title: string;
  };
  type Briefing = {
    date_label: string;
    meetings: BriefingMeeting[];
    agenda_updates: BriefingAgendaUpdate[];
    outstanding_actions: BriefingAction[];
    attention: { overdue_actions: number; overdue_actions_7d: number };
  };
  const [briefing, setBriefing] = useState<Briefing | null>(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefingTz, setBriefingTz] = useState<string>('UTC');
  /**
   * Display timezone for the Today-graph + tooltips. The timeline tick labels (08:00, 09:00…)
   * are derived from `new Date(...).getHours()` which always uses the browser's local TZ, so
   * meeting *bars* sit at their local-time position on the chart. The tooltip used to apply
   * `briefingTz` directly, which is the backend-resolved (personal → workspace → 'UTC')
   * timezone - and that defaulted to 'UTC' for users with neither personal nor workspace TZ
   * configured, giving them "07:00–08:00" in the tooltip while the bar sat at 09:00 on the
   * chart. Treat a 'UTC' backend response as "no preference set" and fall back to the
   * browser's local TZ so the tooltip matches the chart.
   */
  const displayTimezone = useMemo(() => {
    if (briefingTz && briefingTz !== 'UTC') return briefingTz;
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }, [briefingTz]);
  // User's working hours from workspace_members.regional_overrides - drives the Today-graph window.
  const [workingHours, setWorkingHours] = useState<{ start: string; end: string }>({ start: '08:00', end: '19:00' });
  // Upcoming Google-calendar events that aren't enrolled yet (all near-term days), + enrol plumbing.
  const [notEnrolledAll, setNotEnrolledAll] = useState<NotEnrolledEvent[]>([]);
  const [enrollingEventId, setEnrollingEventId] = useState<string | null>(null);
  // Bumped after a successful quick-enrol to refresh the briefing, day-briefings + not-enrolled list.
  const [reloadKey, setReloadKey] = useState(0);
  // Calendar-graph day paging: 0 = today, 1 = tomorrow, … Non-today days are fetched lazily.
  const [timelineDayOffset, setTimelineDayOffset] = useState(0);
  const [hasCalendarConnection, setHasCalendarConnection] = useState(false);
  const [dayBriefings, setDayBriefings] = useState<Record<number, Briefing | null>>({});
  const [dayBriefingLoading, setDayBriefingLoading] = useState(false);

  // User's working hours (and timezone) live in workspace_members.regional_overrides; load once
  // per workspace/user so the Today-graph window matches the personal setting.
  useEffect(() => {
    if (!workspaceId || !user?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('workspace_members')
        .select('regional_overrides')
        .eq('workspace_id', workspaceId)
        .eq('user_id', user.id)
        .maybeSingle();
      if (cancelled) return;
      const ov = (data as { regional_overrides?: Record<string, unknown> | null } | null)?.regional_overrides || {};
      const wh = ov.working_hours as { start?: string; end?: string } | undefined;
      if (wh && typeof wh.start === 'string' && typeof wh.end === 'string') {
        setWorkingHours({ start: wh.start, end: wh.end });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, user?.id]);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    setBriefingLoading(true);
    (async () => {
      try {
        const res = await invokeEdgeFn<{ briefing: Briefing; timezone: string }>('get-daily-briefing', {
          workspace_id: workspaceId,
        });
        if (cancelled) return;
        setBriefing(res.briefing);
        setBriefingTz(res.timezone);
      } catch {
        if (!cancelled) setBriefing(null);
      } finally {
        if (!cancelled) setBriefingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, reloadKey]);

  // Load upcoming not-enrolled calendar events for the "Not enrolled" lane (across the next ~weeks so
  // the calendar-graph day pager has data). Runs independently of the briefing so it never blocks the
  // dashboard. Fetches every healthy provider the user has connected (Google and/or Microsoft) so a
  // Microsoft-only user sees their events too; one provider erroring never wipes the other's feed.
  useEffect(() => {
    if (!workspaceId || !user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const [enrolledMeetingsRes, enrolledSeriesRes, connectionsRes] = await timed(
          'db:dashboard.calendar-prep',
          () =>
            Promise.all([
              supabase.from('meetings').select('calendar_event_id').eq('workspace_id', workspaceId).not('calendar_event_id', 'is', null),
              supabase.from('meeting_series').select('calendar_series_id, external_calendar_uid').eq('workspace_id', workspaceId),
              supabase.from('calendar_connections').select('provider, needs_reconnect').eq('workspace_id', workspaceId).eq('user_id', user.id),
            ]),
        );
        const enrolledIds = new Set<string>();
        for (const m of (enrolledMeetingsRes.data as { calendar_event_id: string | null }[] | null) || []) {
          if (m.calendar_event_id) enrolledIds.add(String(m.calendar_event_id));
        }
        for (const s of (enrolledSeriesRes.data as { calendar_series_id: string | null; external_calendar_uid: string | null }[] | null) || []) {
          if (s.calendar_series_id) enrolledIds.add(String(s.calendar_series_id));
          // external_calendar_uid is stored "<provider>:<id>"; strip whatever prefix is present so the
          // de-dupe works for both google: and microsoft: events.
          const uid = String(s.external_calendar_uid || '');
          const sep = uid.indexOf(':');
          if (sep > 0) enrolledIds.add(uid.slice(sep + 1));
        }

        // Distinct healthy providers this user has connected. Skip any flagged needs_reconnect - the
        // edge function can't mint a token for them and would just 400.
        const providers = Array.from(
          new Set(
            ((connectionsRes.data as { provider: string | null; needs_reconnect: boolean | null }[] | null) || [])
              .filter((c) => !c.needs_reconnect && (c.provider === 'google' || c.provider === 'microsoft'))
              .map((c) => c.provider as 'google' | 'microsoft'),
          ),
        );
        setHasCalendarConnection(providers.length > 0);

        const settled = await Promise.allSettled(
          providers.map(async (provider) => {
            // Served from a 60s cache (lib/calendar-cache) so opening the enrol wizard or hopping
            // back to the dashboard doesn't re-pay the multi-second Google/Graph round-trip.
            const res = await listCalendarEvents(workspaceId, provider);
            return { provider, events: res?.events || [] };
          }),
        );

        const list: NotEnrolledEvent[] = [];
        for (const result of settled) {
          if (result.status !== 'fulfilled') continue; // one provider erroring shouldn't wipe the others
          const { provider, events } = result.value;
          for (const ev of events) {
            // Timed events only - all-day events (start.date, no dateTime) aren't meetings to enrol
            // and would otherwise paint a full-width bar across the day.
            const startIso = (ev.start?.dateTime || null) as string | null;
            if (!startIso || !Number.isFinite(new Date(startIso).getTime())) continue;
            // Google → recurringEventId; Microsoft → seriesMasterId. Same meaning: the parent series id.
            const recurringId =
              (ev.recurringEventId ? String(ev.recurringEventId) : null) ||
              (ev.seriesMasterId ? String(ev.seriesMasterId) : null);
            const id = String(ev.id);
            if (enrolledIds.has(id) || (recurringId && enrolledIds.has(recurringId))) continue;
            list.push({
              id,
              provider,
              summary: (ev.summary as string) || '(No title)',
              starts_at: startIso,
              ends_at: (ev.end?.dateTime || null) as string | null,
              // Both provider branches resolve a join link into `video_url` (native link → location →
              // body), so we don't need provider-specific fields like hangoutLink here.
              video_url: (ev.video_url as string) || null,
              recurring_event_id: recurringId,
              is_organizer: Boolean(ev.is_organizer),
            });
          }
        }
        // Two providers' feeds merged are no longer pre-sorted - order by start so the lane and the
        // day pager read chronologically.
        list.sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
        if (!cancelled) setNotEnrolledAll(list);
      } catch {
        if (!cancelled) setNotEnrolledAll([]); // no calendar connection / API error → just hide the lane
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, user?.id, reloadKey]);

  // Lazily fetch a non-today day's briefing for the calendar-graph pager (today uses `briefing`).
  useEffect(() => {
    if (!workspaceId || timelineDayOffset === 0) return;
    if (dayBriefings[timelineDayOffset]) return; // cached
    let cancelled = false;
    setDayBriefingLoading(true);
    (async () => {
      try {
        const res = await invokeEdgeFn<{ briefing: Briefing }>('get-daily-briefing', {
          workspace_id: workspaceId,
          day_offset: timelineDayOffset,
        });
        if (!cancelled) setDayBriefings((m) => ({ ...m, [timelineDayOffset]: res.briefing }));
      } catch {
        if (!cancelled) setDayBriefings((m) => ({ ...m, [timelineDayOffset]: null }));
      } finally {
        if (!cancelled) setDayBriefingLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // dayBriefings intentionally omitted - the cache check reads the current snapshot and the
    // cache only grows; reloadKey forces a refetch after an enrol.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, timelineDayOffset, reloadKey]);

  // Workspace default capture method for a newly enrolled series (PRD_DEVICE_AUTO_RECORD §4.3).
  // The dashboard only gets workspaceId, so read the row on demand - this runs once per enrol click.
  const workspaceCaptureDefault = useCallback(async (wsId: string): Promise<'bot' | 'desktop'> => {
    const { data } = await supabase.from('workspaces').select('settings').eq('id', wsId).maybeSingle();
    const settings = (data as { settings?: Record<string, unknown> | null } | null)?.settings;
    return settings?.default_capture_method === 'desktop' ? 'desktop' : 'bot';
  }, []);

  // 1-click enrol: create the series + this occurrence with the same defaults the wizard uses, then
  // pull invitees via calendar-sync-meeting. Does NOT modify the user's calendar event.
  const quickEnrol = useCallback(
    async (ev: NotEnrolledEvent) => {
      if (!workspaceId || !user?.id) return;
      setEnrollingEventId(ev.id);
      try {
        // Key the series by the recurring MASTER (not the per-occurrence id) so every occurrence of a
        // recurring meeting maps to ONE series instead of fragmenting into one series per occurrence.
        const master = ev.recurring_event_id?.trim() || null;
        const externalUid = master ? `${ev.provider}:${master}` : `${ev.provider}:${ev.id}`;
        // Match an existing series by that uid OR by the recurring master (auto-enrolled series carry a
        // per-occurrence external_calendar_uid but the master in calendar_series_id).
        const { data: existingSeries } = await supabase
          .from('meeting_series')
          .select('id')
          .eq('workspace_id', workspaceId)
          .or(`external_calendar_uid.eq.${externalUid}${master ? `,calendar_series_id.eq.${master}` : ''}`)
          .limit(1)
          .maybeSingle();

        let seriesId: string | null = existingSeries?.id ?? null;
        let createdNewSeries = false;
        if (!seriesId) {
          const { data: seriesRow, error: se } = await supabase
            .from('meeting_series')
            .insert({
              workspace_id: workspaceId,
              owner_user_id: user.id,
              owner_is_provisional: !ev.is_organizer,
              title: ev.summary,
              meeting_type: 'team',
              is_recurring_series: Boolean(master),
              external_calendar_uid: externalUid,
              calendar_provider: ev.provider,
              calendar_series_id: master,
              enrolment_source: 'oauth_picker',
              created_by: user.id,
              // Bot joins by default; calendar-sync-meeting only schedules it when there's a join link.
              transcript_bot_enabled: true,
              // How it gets captured follows the workspace default (PRD_DEVICE_AUTO_RECORD §4.3).
              capture_method: await workspaceCaptureDefault(workspaceId),
              check_in_config: {},
              permissions: {
                enrolment: {
                  capture_mode: 'passive',
                  slack_digest: true,
                  email_confirmation: true,
                  guest_editing: false,
                  external_recording: true,
                  anonymous_feedback: true,
                },
              },
            })
            .select('id')
            .single();
          // A concurrent enrol created the same series first (uniq_meeting_series_external) - reuse it.
          if ((se as { code?: string } | null)?.code === '23505') {
            const { data: r } = await supabase
              .from('meeting_series')
              .select('id')
              .eq('workspace_id', workspaceId)
              .eq('external_calendar_uid', externalUid)
              .maybeSingle();
            seriesId = r?.id ?? null;
          } else if (se || !seriesRow) {
            throw se || new Error('Failed to create series');
          } else {
            seriesId = seriesRow.id;
            createdNewSeries = true;
          }
        }
        if (!seriesId) throw new Error('Failed to resolve series');

        const { data: meetingRows, error: me } = await supabase
          .from('meetings')
          .insert([
            {
              series_id: seriesId,
              workspace_id: workspaceId,
              starts_at: ev.starts_at,
              ends_at: ev.ends_at,
              video_meeting_url: ev.video_url,
              calendar_event_id: ev.id,
              status: 'scheduled',
            },
          ])
          .select('id');
        // This exact occurrence is already enrolled (uniq_meetings_workspace_calendar_event, 0118) -
        // e.g. the series was enrolled via the wizard and we matched it by master above.
        if ((me as { code?: string } | null)?.code === '23505') {
          toast.info('Already enrolled');
          setReloadKey((k) => k + 1);
          return;
        }
        if (me || !meetingRows?.length) {
          if (createdNewSeries) await supabase.from('meeting_series').delete().eq('id', seriesId);
          throw me || new Error('Failed to create meeting');
        }

        try {
          await invokeEdgeFn('calendar-sync-meeting', {
            meeting_id: meetingRows[0].id,
            workspace_id: workspaceId,
            sync_all_in_series: false,
          });
        } catch {
          /* invitee sync is best-effort */
        }
        track('meeting_enrolled', { source: 'quick', recurring: Boolean(master), has_video_url: Boolean(ev.video_url) });
        toast.success(`Enrolled “${ev.summary}”`);
        setDayBriefings({}); // drop cached day briefings so the newly-enrolled meeting shows up
        setReloadKey((k) => k + 1);
      } catch (e: any) {
        toast.error(e?.message || 'Enrol failed');
      } finally {
        setEnrollingEventId(null);
      }
    },
    [workspaceId, user?.id],
  );

  // ── Calendar-graph day pager: derived data for the selected day ──
  const displayedBriefing = timelineDayOffset === 0 ? briefing : dayBriefings[timelineDayOffset] ?? null;
  const timelineMeetings = useMemo(() => {
    const b = displayedBriefing;
    if (!b) return [];
    // My outstanding actions per series → series-wide count for the meeting tooltip.
    const byTitle = new Map<string, number>();
    for (const a of b.outstanding_actions || []) {
      const k = a.meeting_title || 'Meeting';
      byTitle.set(k, (byTitle.get(k) || 0) + 1);
    }
    return (b.meetings || []).map((m) => ({ ...m, my_open_action_count: byTitle.get(m.series_title) || 0 }));
  }, [displayedBriefing]);
  const timelineNotEnrolled = useMemo(() => {
    const start = startOfLocalDay(new Date());
    start.setDate(start.getDate() + timelineDayOffset);
    const end = endOfLocalDay(new Date());
    end.setDate(end.getDate() + timelineDayOffset);
    const lo = start.getTime();
    const hi = end.getTime();
    return notEnrolledAll.filter((e) => {
      const t = new Date(e.starts_at).getTime();
      return t >= lo && t <= hi;
    });
  }, [notEnrolledAll, timelineDayOffset]);
  const onPrevTimelineDay = useCallback(() => setTimelineDayOffset((o) => Math.max(0, o - 1)), []);
  const onNextTimelineDay = useCallback(() => setTimelineDayOffset((o) => Math.min(14, o + 1)), []);

  const loadCheckInList = useCallback(
    async (meetingId: string) => {
      if (!user?.id) return;
      const { data } = await supabase
        .from('pre_meeting_check_ins')
        .select('id, meeting_id, user_id, body_text, content_type, media_path, media_mime, transcript_text, transcript_status, submitted_at')
        .eq('meeting_id', meetingId)
        .eq('user_id', user.id)
        .order('submitted_at', { ascending: false });
      setCheckInList(data ?? []);
    },
    [user?.id],
  );

  const openCheckInModal = useCallback(
    async (m: DashboardMeeting) => {
      if (!user?.id) {
        toast.error('Sign in to submit a check-in.');
        return;
      }
      setCheckInMeeting(m);
      setCheckInList([]);
      setCheckInLoading(true);
      await loadCheckInList(m.id);
      setCheckInLoading(false);
    },
    [user?.id, loadCheckInList],
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!workspaceId) {
        if (!cancelled) {
          setLoading(false);
          setHasActivity(false);
        }
        return;
      }

      setLoading(true);
      const todayStart = startOfLocalDay(new Date());
      const todayEnd = endOfLocalDay(new Date());

      const { data: meetingRows, error: mErr } = await supabase
        .from('meetings')
        .select(
          `
          id,
          series_id,
          starts_at,
          ends_at,
          status,
          updated_at,
          meeting_series ( title )
        `,
        )
        .eq('workspace_id', workspaceId)
        .order('starts_at', { ascending: false })
        .limit(120);

      if (mErr || cancelled) {
        if (!cancelled) {
          setLoading(false);
          setHasActivity(false);
        }
        return;
      }

      // Scope the dashboard to the user's OWN meetings (owner or attendee). A Shared workspace makes
      // other people's meetings visible via RLS, but the dashboard + info blocks are a personal view.
      const myEmailForScope = (userProfile?.email || '').trim().toLowerCase();
      const attendeeOr = [
        user?.id ? `user_id.eq.${user.id}` : '',
        myEmailForScope ? `email.eq.${myEmailForScope}` : '',
      ]
        .filter(Boolean)
        .join(',');
      const [{ data: ownedSeriesRows }, { data: attendedRows }] = await Promise.all([
        supabase.from('meeting_series').select('id').eq('workspace_id', workspaceId).eq('owner_user_id', user?.id || ''),
        attendeeOr
          ? supabase
              .from('meeting_attendees')
              .select('meeting_id, meetings!inner(workspace_id)')
              .eq('meetings.workspace_id', workspaceId)
              .or(attendeeOr)
          : Promise.resolve({ data: [] as Array<{ meeting_id: string }> }),
      ]);
      const myOwnedSeriesIds = new Set(((ownedSeriesRows || []) as Array<{ id: string }>).map((r) => r.id));
      const myAttendedMeetingIds = new Set(
        ((attendedRows || []) as Array<{ meeting_id: string }>).map((r) => r.meeting_id),
      );
      const isMineMeeting = (m: any): boolean =>
        Boolean(m) && (myOwnedSeriesIds.has(m.series_id) || myAttendedMeetingIds.has(m.id));

      const meetings = ((meetingRows || []) as DashboardMeeting[]).filter(isMineMeeting);

      // Today's count + the spotlight pool come from a bounded window around now, NOT the main
      // 120-cap list. That list is ordered by starts_at desc, so in workspaces with many future
      // recurring occurrences it holds only the farthest-future rows and misses today entirely
      // (which is why the header read "Nothing on the calendar today" with meetings present).
      const nearTermStartIso = new Date(Date.now() - 2 * 86400000).toISOString();
      const nearTermEndIso = new Date(Date.now() + 30 * 86400000).toISOString();
      const { data: nearTermRows } = await supabase
        .from('meetings')
        .select('id, series_id, starts_at, ends_at, status, updated_at, meeting_series ( title )')
        .eq('workspace_id', workspaceId)
        .gte('starts_at', nearTermStartIso)
        .lte('starts_at', nearTermEndIso)
        .order('starts_at', { ascending: true })
        .limit(300);
      const nearTermMeetings = (((nearTermRows || []) as unknown) as DashboardMeeting[]).filter(isMineMeeting);

      const todayN = nearTermMeetings.filter((m) => {
        if (!m.starts_at) return false;
        const t = new Date(m.starts_at);
        return t >= todayStart && t <= todayEnd;
      }).length;

      const meetingIds = meetings.map((m) => m.id);

      // "Last week" = rolling 7-day window ending now, so we always have something to show
      // even on a Monday morning before any current-week activity has happened.
      const lastWeekEndMs = Date.now();
      const lastWeekStartMs = lastWeekEndMs - 7 * 86400000;
      const lastWeekStartIso = new Date(lastWeekStartMs).toISOString();
      const lastWeekEndIso = new Date(lastWeekEndMs).toISOString();

      // Dedicated fetch for last-week meetings - workspaces with many future-scheduled meetings
      // would otherwise miss last week's because the main query is capped at 120 rows ordered by
      // starts_at desc (i.e. future-heavy).
      const { data: weekRows } = await supabase
        .from('meetings')
        .select('id, series_id, starts_at, ends_at, status, updated_at, meeting_series ( title )')
        .eq('workspace_id', workspaceId)
        .gte('starts_at', lastWeekStartIso)
        .lte('starts_at', lastWeekEndIso)
        .order('starts_at', { ascending: false });
      const weekMeetings = (((weekRows || []) as unknown) as DashboardMeeting[]).filter(isMineMeeting);
      const weekMeetingIds = weekMeetings.map((m) => m.id);

      let openCount = 0;
      let mineOpen = 0;
      let actionAgeBucketsLocal: number[] = [0, 0, 0, 0, 0];
      let weeklyMeetingsCompleted = 0;
      let weeklyActionsClosed = 0;
      let weeklyDecisions = 0;
      let topActive: { meeting_id: string; title: string; starts_at: string | null; actions: number; agenda: number; decisions: number; total: number }[] = [];

      // Use an embedded inner-join filter so we hit every workspace meeting - NOT just the
      // top-120 by starts_at desc. Future-heavy workspaces would otherwise hide all open actions
      // that belong to past meetings.
      {
        const { count: openCt } = await supabase
          .from('action_items')
          .select('id, meetings!inner(workspace_id)', { count: 'exact', head: true })
          .eq('meetings.workspace_id', workspaceId)
          .in('status', ['open', 'in_progress']);

        openCount = openCt ?? 0;

        // Personal open count: assignee_user_id matches, OR (legacy) name/email matches.
        const userFullName = (userProfile?.full_name || '').trim();
        const userEmail = (userProfile?.email || '').trim();
        const orParts: string[] = [];
        if (user?.id) orParts.push(`assignee_user_id.eq.${user.id}`);
        if (userFullName) orParts.push(`assignee_name.ilike.${userFullName.replace(/,/g, ' ')}`);
        if (userEmail) orParts.push(`assignee_email.ilike.${userEmail}`);
        if (orParts.length > 0) {
          const { data: mineRows, count: mineCt } = await supabase
            .from('action_items')
            .select('id, created_at, meetings!inner(workspace_id)', { count: 'exact' })
            .eq('meetings.workspace_id', workspaceId)
            .in('status', ['open', 'in_progress'])
            .or(orParts.join(','))
            .limit(500);
          mineOpen = mineCt ?? 0;
          // Bucket the rows by age: [0–1d, 2–7d, 8–14d, 15–30d, 30+d]. Older = darker shade.
          const buckets = [0, 0, 0, 0, 0];
          const nowMs = Date.now();
          for (const r of ((mineRows as unknown) || []) as Array<{ created_at: string | null }>) {
            const t = r?.created_at ? new Date(r.created_at).getTime() : NaN;
            if (!Number.isFinite(t)) continue;
            const days = (nowMs - t) / 86_400_000;
            const idx = days > 30 ? 4 : days > 14 ? 3 : days > 7 ? 2 : days > 1 ? 1 : 0;
            buckets[idx]++;
          }
          actionAgeBucketsLocal = buckets;
        }
      }

      if (meetingIds.length) {

        // Weekly stats - "meetings happened" = meetings whose starts_at falls in the rolling
        // 7-day window (status='completed' is unreliable since most workspaces never advance the
        // status field). Actions closed + decisions are scoped to those week-meeting ids so we're
        // not bounded by the 120-cap on the main meetings list.
        weeklyMeetingsCompleted = weekMeetings.length;

        if (weekMeetingIds.length > 0) {
          const { count: weekActionsClosedC } = await supabase
            .from('action_items')
            .select('id', { count: 'exact', head: true })
            .in('meeting_id', weekMeetingIds)
            .eq('status', 'done')
            .gte('updated_at', lastWeekStartIso)
            .lte('updated_at', lastWeekEndIso);
          weeklyActionsClosed = weekActionsClosedC ?? 0;

          const { count: weekDecisionsC } = await supabase
            .from('decisions')
            .select('id', { count: 'exact', head: true })
            .in('meeting_id', weekMeetingIds)
            .gte('created_at', lastWeekStartIso)
            .lte('created_at', lastWeekEndIso);
          weeklyDecisions = weekDecisionsC ?? 0;
        }

        // Most active meetings last week - meetings whose starts_at is within the rolling
        // 7-day window, ranked by (action_items + agenda_items + decisions) attached to them.
        const weeklyMeetingsForActivity = weekMeetings;
        const weeklyIds = weekMeetingIds;
        if (weeklyIds.length > 0) {
          const [aiRes, agRes, dcRes] = await Promise.all([
            supabase.from('action_items').select('meeting_id').in('meeting_id', weeklyIds).neq('status', 'dropped'),
            supabase.from('agenda_items').select('meeting_id').in('meeting_id', weeklyIds),
            supabase.from('decisions').select('meeting_id').in('meeting_id', weeklyIds),
          ]);
          const counts = new Map<string, { actions: number; agenda: number; decisions: number }>();
          const ensure = (id: string) => {
            let c = counts.get(id);
            if (!c) {
              c = { actions: 0, agenda: 0, decisions: 0 };
              counts.set(id, c);
            }
            return c;
          };
          for (const r of (aiRes.data || []) as { meeting_id: string }[]) ensure(r.meeting_id).actions++;
          for (const r of (agRes.data || []) as { meeting_id: string }[]) ensure(r.meeting_id).agenda++;
          for (const r of (dcRes.data || []) as { meeting_id: string }[]) ensure(r.meeting_id).decisions++;
          topActive = weeklyMeetingsForActivity
            .map((m) => {
              const c = counts.get(m.id) || { actions: 0, agenda: 0, decisions: 0 };
              return {
                meeting_id: m.id,
                title: seriesTitle(m),
                starts_at: m.starts_at,
                actions: c.actions,
                agenda: c.agenda,
                decisions: c.decisions,
                total: c.actions + c.agenda + c.decisions,
              };
            })
            .filter((r) => r.total > 0)
            .sort((a, b) => b.total - a.total)
            .slice(0, 4);
        }
      }

      const spotlight = pickSpotlightMeetings(nearTermMeetings);

      const active = nearTermMeetings.length > 0 || meetings.length > 0 || openCount > 0;

      if (!cancelled) {
        setMeetingsTodayCount(todayN);
        setOpenActionCount(openCount);
        setMineOpenActionCount(mineOpen);
        setActionAgeBuckets(actionAgeBucketsLocal);
        setSpotlightMeetings(spotlight);
        setTopActiveMeetings(topActive);
        setWeeklyStats({
          meetings: weeklyMeetingsCompleted,
          actionsClosed: weeklyActionsClosed,
          decisions: weeklyDecisions,
        });
        setHasActivity(active);
        setLoading(false);
      }
    }

    void timed('dashboard:data-load', () => load());
    return () => {
      cancelled = true;
    };
  }, [workspaceId, user?.id, userProfile?.full_name, userProfile?.email]);

  const summarySentence = useMemo(() => {
    const m = meetingsTodayCount;
    const a = mineOpenActionCount;
    if (m > 0 && a > 0) {
      return (
        <>
          <strong className="text-headline">{m} meeting{m === 1 ? '' : 's'}</strong> on your plate today and{' '}
          <strong className="text-headline">{a} action item{a === 1 ? '' : 's'}</strong> waiting on you.
        </>
      );
    }
    if (m > 0 && a === 0) {
      return (
        <>
          <strong className="text-headline">{m} meeting{m === 1 ? '' : 's'}</strong> on your plate today - no open
          actions of your own, so stay present in the room.
        </>
      );
    }
    if (m === 0 && a > 0) {
      return (
        <>
          Nothing on the calendar today. <strong className="text-headline">{a} action item{a === 1 ? '' : 's'}</strong>{' '}
          assigned to you - a good time to chip away.
        </>
      );
    }
    return (
      <>
        A clear day. Skim a past session, tidy a few action items, or ask Upstand what&apos;s worth your time.
      </>
    );
  }, [meetingsTodayCount, mineOpenActionCount]);

  const ambient = (
    <>
      <div
        className="pointer-events-none absolute top-20 right-10 h-96 w-96 rounded-full bg-primary-container/15 blur-[100px]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute bottom-32 left-8 h-64 w-64 rounded-full bg-tertiary-container/10 blur-[80px]"
        aria-hidden
      />
    </>
  );

  if (loading && workspaceId) {
    return (
      <div className="relative min-h-full pb-28">
        {ambient}
        <AppTopBar
          userProfile={userProfile}
          workspaceName={workspaceName}
          onHelp={onHelp}
          onSignOut={onSignOut}
          onSwitchWorkspace={onSwitchWorkspace}
          onConnectCalendar={onNavigateToIntegrations}
        />
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-center gap-4 px-2 py-32">
          <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden />
          <p className="text-sm text-muted-foreground">Loading your workspace…</p>
        </div>
      </div>
    );
  }

  if (hasActivity) {
    return (
      <div className="relative min-h-full pb-28">
        {ambient}
        <AppTopBar
          userProfile={userProfile}
          workspaceName={workspaceName}
          onHelp={onHelp}
          onSignOut={onSignOut}
          onSwitchWorkspace={onSwitchWorkspace}
          onConnectCalendar={onNavigateToIntegrations}
        />

        <div className="mx-auto max-w-[1400px] px-2 sm:px-4 lg:px-6">
          <section className="mb-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h1 className="font-display text-2xl font-semibold leading-tight tracking-tight text-headline md:text-3xl">
                  {greetingLabel(new Date())}, {firstName}<span className="text-primary">.</span>
                </h1>
                <p className="mt-1.5 max-w-2xl font-sans text-sm font-light leading-relaxed text-muted-foreground">
                  {summarySentence}
                </p>
              </div>
              <Button
                type="button"
                onClick={onNewMeeting}
                className="h-auto shrink-0 rounded-lg bg-gradient-to-br from-primary to-primary-container px-6 py-4 text-base font-medium text-primary-foreground shadow-lg shadow-primary/20 transition-transform hover:scale-[1.01]"
              >
                <Plus className="mr-2 h-4 w-4" />
                Enrol meeting
              </Button>
            </div>
          </section>

          {(briefingLoading || briefing) && (
            <section className="mb-8">
              <DailyBriefingPanel
                briefing={briefing}
                loading={briefingLoading}
                timezone={displayTimezone}
                actionAgeBuckets={actionAgeBuckets}
                onOpenMeeting={onOpenMeetingSession}
                onAllActions={onNavigateToActionItems}
                notEnrolled={timelineNotEnrolled}
                onEnrol={quickEnrol}
                enrollingId={enrollingEventId}
                timelineMeetings={timelineMeetings}
                dayOffset={timelineDayOffset}
                onPrevDay={onPrevTimelineDay}
                onNextDay={onNextTimelineDay}
                dayLoading={dayBriefingLoading}
              workingHours={workingHours}
              hasCalendarConnection={hasCalendarConnection}
              onEnrolMeeting={onNewMeeting}
              onConnectCalendar={onNavigateToIntegrations}
              />
            </section>
          )}

          <div className="space-y-6">
            {/* Upcoming spotlight meetings - surfaced first so the next sessions lead the page. */}
            {spotlightMeetings.length > 0 && (
              <section>
                <div className="grid gap-4 md:grid-cols-2">
                  {spotlightMeetings.map((m) => {
                    const badge = meetingSpotlightBadge(m);
                    const upcoming = isMeetingUpcoming(m);
                    const when = formatSpotlightDateTime(m.starts_at);
                    return (
                      <div
                        key={m.id}
                        className="group flex flex-col rounded-lg border border-outline-variant/15 bg-card p-6 text-left shadow-sm transition-all hover:border-primary/25 hover:shadow-md"
                      >
                        <span className={cn('mb-3 text-[10px] font-bold uppercase tracking-wider', badge.className)}>
                          {badge.label}
                        </span>
                        <h3 className="mb-1 font-display text-lg font-semibold text-headline group-hover:text-primary">
                          {seriesTitle(m)}
                        </h3>
                        {when && (
                          <p className="mb-3 flex items-center gap-1.5 text-xs font-medium text-tertiary">
                            <Calendar className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                            {when}
                          </p>
                        )}
                        <p className="mb-4 line-clamp-2 flex-1 text-sm text-tertiary">
                          Stay aligned on decisions and next steps for this session.
                        </p>
                        <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-outline-variant/10 pt-4">
                          <div className="flex -space-x-2">
                            {['S', 'M', 'A'].map((x, i) => (
                              <div
                                key={i}
                                className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-background bg-muted text-[10px] font-semibold text-muted-foreground"
                              >
                                {x}
                              </div>
                            ))}
                          </div>
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            {upcoming && (
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="gap-1.5"
                                onClick={() => void openCheckInModal(m)}
                              >
                                <MessageSquare className="h-3.5 w-3.5" strokeWidth={2} />
                                Pre-check-in
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="gap-1"
                              onClick={() =>
                                onOpenMeetingSession
                                  ? onOpenMeetingSession(m.id)
                                  : onNavigateToMeetings?.()
                              }
                            >
                              View session
                              <ArrowRight className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Most-active list on the left, stats + Ask Upstand stacked on the right. */}
            <div className="grid grid-cols-12 gap-6">
              {/* Most active last week */}
              <div className="col-span-12 rounded-lg border border-white/40 bg-white/40 p-8 shadow-sm backdrop-blur-xl lg:col-span-8">
                <h3 className="mb-1 font-display text-lg font-semibold text-headline">Most active last week</h3>
                <p className="mb-5 text-xs text-tertiary">By action items, agenda updates &amp; decisions captured.</p>
                {topActiveMeetings.length === 0 ? (
                  <p className="text-sm text-tertiary">No meetings last week.</p>
                ) : (
                  <ul className="space-y-3">
                    {topActiveMeetings.map((row) => (
                      <li key={row.meeting_id}>
                        <button
                          type="button"
                          onClick={() => (onOpenMeetingSession ? onOpenMeetingSession(row.meeting_id) : onNavigateToMeetings?.())}
                          className="group flex w-full items-start gap-3 rounded-md px-1 py-1 text-left transition-colors hover:bg-muted/40"
                        >
                          <div className="flex min-w-[40px] flex-col items-center rounded-md bg-primary/10 px-2 py-1 text-primary">
                            <span className="text-base font-semibold leading-none">{row.total}</span>
                            <span className="mt-0.5 text-[9px] font-medium uppercase tracking-wider">items</span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-headline group-hover:text-primary">
                              {row.title}
                            </p>
                            <p className="mt-0.5 text-[11px] text-tertiary">
                              {row.actions} action{row.actions === 1 ? '' : 's'} · {row.agenda} agenda · {row.decisions} decision{row.decisions === 1 ? '' : 's'}
                            </p>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Right column: stats + Ask Upstand */}
              <div className="col-span-12 flex flex-col gap-6 lg:col-span-4">
              {/* This week at a glance - replaces Focus score */}
              <div className="rounded-lg border border-outline-variant/10 bg-surface-container-low p-6">
                <h3 className="mb-1 font-display text-lg font-semibold text-headline">Last week</h3>
                <p className="mb-4 text-xs text-tertiary">Activity rolled up across the workspace.</p>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="rounded-lg bg-white/60 p-3">
                    <Video className="mx-auto mb-1 h-4 w-4 text-primary" strokeWidth={1.75} />
                    <div className="font-display text-2xl font-light leading-none text-headline">{weeklyStats.meetings}</div>
                    <div className="mt-1 text-[10px] font-medium uppercase tracking-wider text-tertiary">Meetings</div>
                  </div>
                  <div className="rounded-lg bg-white/60 p-3">
                    <CheckCircle2 className="mx-auto mb-1 h-4 w-4 text-emerald-600" strokeWidth={1.75} />
                    <div className="font-display text-2xl font-light leading-none text-headline">{weeklyStats.actionsClosed}</div>
                    <div className="mt-1 text-[10px] font-medium uppercase tracking-wider text-tertiary">Closed</div>
                  </div>
                  <div className="rounded-lg bg-white/60 p-3">
                    <Gavel className="mx-auto mb-1 h-4 w-4 text-amber-700" strokeWidth={1.75} />
                    <div className="font-display text-2xl font-light leading-none text-headline">{weeklyStats.decisions}</div>
                    <div className="mt-1 text-[10px] font-medium uppercase tracking-wider text-tertiary">Decisions</div>
                  </div>
                </div>
              </div>

              {/* Ask Upstand - replaces Luminous AI, lighter colour, opens AI modal */}
              <div className="relative overflow-hidden rounded-2xl border border-stone-700/60 bg-gradient-to-br from-stone-700 via-stone-700 to-stone-800 p-8 text-stone-100 shadow-lg">
                <div
                  className="pointer-events-none absolute -right-8 -top-12 h-40 w-40 rounded-full bg-primary/20 blur-3xl"
                  aria-hidden
                />
                <div className="relative">
                  <h3 className="mb-1 flex items-center gap-2 font-display text-lg font-semibold text-white">
                    <Sparkles className="h-4 w-4 text-amber-300" strokeWidth={2} />
                    Ask Upstand
                  </h3>
                  <p className="mb-6 text-sm leading-relaxed text-stone-200/95">
                    {mineOpenActionCount > 0
                      ? `You have ${mineOpenActionCount} open action item${mineOpenActionCount === 1 ? '' : 's'} - ask what to tackle first, or who said what in a recent session.`
                      : 'Ask what happened in any meeting, who said what, or what you still owe people.'}
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    className="w-full bg-white/15 text-white hover:bg-white/25"
                    onClick={() => openAgent()}
                  >
                    Ask Upstand
                    <span className="ml-2 hidden rounded border border-white/20 px-1.5 py-0.5 font-mono text-[10px] text-white/70 sm:inline">
                      ⌘J
                    </span>
                  </Button>
                </div>
              </div>
              </div>
            </div>

          </div>
        </div>

        <Dialog
          open={checkInMeeting != null}
          onOpenChange={(open) => {
            if (!open) {
              setCheckInMeeting(null);
              setCheckInList([]);
              setCheckInLoading(false);
            }
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Pre-check-in</DialogTitle>
              <DialogDescription>
                {checkInMeeting ? (
                  <>
                    <span className="font-medium text-foreground">{seriesTitle(checkInMeeting)}</span>
                    {checkInMeeting.starts_at ? (
                      <span className="mt-1 block text-muted-foreground">
                        {new Date(checkInMeeting.starts_at).toLocaleString(undefined, {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </span>
                    ) : null}
                  </>
                ) : null}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {checkInLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                  Loading…
                </div>
              ) : checkInMeeting && user?.id ? (
                <>
                  <div className="space-y-2">
                    <Label>Add an update</Label>
                    <CheckInRecorder
                      meetingId={checkInMeeting.id}
                      userId={user.id}
                      onSubmitted={() => loadCheckInList(checkInMeeting.id)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Your submissions</Label>
                    <div className="max-h-72 overflow-y-auto">
                      <CheckInList
                        items={checkInList}
                        emptyText="No updates yet."
                        currentUserId={user.id}
                        onChanged={() => loadCheckInList(checkInMeeting.id)}
                      />
                    </div>
                  </div>
                </>
              ) : null}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCheckInMeeting(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="relative min-h-full pb-28">
      {ambient}

      <AppTopBar
        userProfile={userProfile}
        workspaceName={workspaceName}
        onHelp={onHelp}
        onSignOut={onSignOut}
        onSwitchWorkspace={onSwitchWorkspace}
        onConnectCalendar={onNavigateToIntegrations}
      />

      <div className="mx-auto max-w-[1400px] px-2 sm:px-4 lg:px-6">
        <section className="mb-12">
          <h1 className="mb-2 font-display text-[2.75rem] font-light leading-none tracking-tight text-foreground md:text-[3.5rem]">
            Welcome, {firstName}.
          </h1>
          <p className="max-w-xl font-sans text-lg font-light leading-relaxed text-muted-foreground">
            Step into your workspace. Your personal AI is ready to distill your conversations into clarity.
          </p>
        </section>

        {(briefingLoading || briefing) && (
          <section className="mb-10">
            <DailyBriefingPanel
              briefing={briefing}
              loading={briefingLoading}
              timezone={displayTimezone}
              actionAgeBuckets={actionAgeBuckets}
              onOpenMeeting={onOpenMeetingSession}
              onAllActions={onNavigateToActionItems}
              notEnrolled={timelineNotEnrolled}
              onEnrol={quickEnrol}
              enrollingId={enrollingEventId}
              timelineMeetings={timelineMeetings}
              dayOffset={timelineDayOffset}
              onPrevDay={onPrevTimelineDay}
              onNextDay={onNextTimelineDay}
              dayLoading={dayBriefingLoading}
              workingHours={workingHours}
              hasCalendarConnection={hasCalendarConnection}
              onEnrolMeeting={onNewMeeting}
              onConnectCalendar={onNavigateToIntegrations}
            />
          </section>
        )}

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-8">
            <div className="group relative flex h-full min-h-[280px] flex-col justify-between overflow-hidden rounded-lg border border-white/40 bg-white/40 p-10 shadow-sm backdrop-blur-xl transition-transform duration-300 hover:shadow-xl hover:shadow-primary/5">
              <div className="relative z-10">
                <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-primary-container/20">
                  <Video className="h-8 w-8 text-primary" strokeWidth={1.5} />
                </div>
                <h2 className="mb-4 font-display text-2xl font-medium text-headline md:text-3xl">
                  Start your first meeting journey
                </h2>
                <p className="mb-8 max-w-md font-sans text-lg leading-relaxed text-stone-600">
                  Connect your calendar or upload a recording. Upstand will handle the notes, so you can focus on the
                  connection.
                </p>
                <Button
                  type="button"
                  onClick={onNewMeeting ?? onNavigateToMeetings}
                  className="h-auto rounded-md bg-gradient-to-br from-primary to-primary-container px-8 py-4 text-base font-medium text-white shadow-lg shadow-primary/20 transition-transform hover:scale-[1.02]"
                >
                  <span className="flex items-center gap-3">
                    <PlayCircle className="h-5 w-5" />
                    Enrol meeting
                  </span>
                </Button>
              </div>
              <div
                className="pointer-events-none absolute -bottom-[10%] -right-[10%] h-80 w-80 opacity-20 transition-transform duration-700 group-hover:scale-110"
                aria-hidden
              >
                <div className="h-full w-full rounded-full border-[32px] border-amber-600/80" />
              </div>
            </div>
          </div>

          <div className="col-span-12 flex flex-col gap-6 lg:col-span-4">
            <div className="flex flex-1 flex-col rounded-lg border border-white/40 bg-white/40 p-8 shadow-sm backdrop-blur-xl">
              <div className="mb-6 flex items-center gap-3">
                <ListTodo className="h-5 w-5 text-tertiary" strokeWidth={1.75} />
                <h3 className="font-display text-lg font-semibold text-foreground">Action items</h3>
              </div>
              <div className="flex flex-1 flex-col items-center justify-center py-8 text-center">
                <Inbox className="mb-4 h-14 w-14 text-stone-300" strokeWidth={1} />
                <p className="max-w-[220px] px-2 font-sans text-sm text-stone-500">
                  Your clear path forward starts with your first session.
                </p>
              </div>
            </div>

            <div className="rounded-lg border-b-2 border-outline-variant/10 bg-surface-container-low p-8">
              <h3 className="mb-4 font-display text-lg font-semibold text-foreground">Quick connect</h3>
              <div className="flex gap-4">
                <button
                  type="button"
                  className="flex h-12 w-12 items-center justify-center rounded-lg bg-white shadow-sm transition-colors hover:bg-stone-50"
                  aria-label="Calendar"
                >
                  <Calendar className="h-6 w-6 text-stone-600" />
                </button>
                <button
                  type="button"
                  className="flex h-12 w-12 items-center justify-center rounded-lg bg-white shadow-sm transition-colors hover:bg-stone-50"
                  aria-label="Video"
                >
                  <Video className="h-6 w-6 text-stone-600" />
                </button>
                <button
                  type="button"
                  className="flex h-12 w-12 items-center justify-center rounded-lg bg-white shadow-sm transition-colors hover:bg-stone-50"
                  aria-label="Cloud"
                >
                  <Cloud className="h-6 w-6 text-stone-600" />
                </button>
              </div>
            </div>
          </div>

          <div className="col-span-12">
            <div className="rounded-lg border border-white/40 bg-white/40 p-10 shadow-sm backdrop-blur-xl">
              <div className="mb-10 flex items-center justify-between">
                <h3 className="font-display text-xl font-semibold text-foreground">Recent activity</h3>
                <button
                  type="button"
                  onClick={onNavigateToMeetings}
                  className="font-sans text-sm font-medium text-primary hover:underline"
                >
                  View meetings
                </button>
              </div>
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-outline-variant/30 bg-background/30 py-20">
                <div className="relative mb-6">
                  <div className="flex h-20 w-20 items-center justify-center rounded-full bg-surface-container-low">
                    <BookOpen className="h-10 w-10 text-stone-300" strokeWidth={1.25} />
                  </div>
                  <div className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full bg-amber-100">
                    <Sparkles className="h-4 w-4 text-amber-700" />
                  </div>
                </div>
                <h4 className="mb-2 font-display font-medium text-stone-800">The pages are waiting to be written</h4>
                <p className="max-w-sm text-center font-sans text-stone-500">
                  Once you start a meeting, your AI assistant will populate this timeline with key moments, summaries,
                  and growth milestones.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}
